import React, { useEffect, useId, useState } from 'react'
import { apiJson } from '../api/client'
import '../styles/components/fan-preferences.css'

const labels = {
  model_led_preference: 'Character takes the initiative', fan_led_preference: 'Fan leads the conversation',
  playful_challenge_preference: 'Playful challenge', co_creation_preference: 'Creating together',
  ritual_preference: 'Familiar routines', spontaneity_preference: 'Variety and spontaneity',
  appreciation_preference: 'Specific appreciation', reflective_conversation_preference: 'Deeper conversation',
  concise_exchange_preference: 'Direct, concise exchanges', autonomy_preference: 'Choice and autonomy',
  autonomy_sensitivity: 'Autonomy', caregiving_orientation: 'Caring gestures', closeness_seeking: 'Closeness',
  need_to_be_needed: 'Wish to feel useful', validation_need: 'Recognition',
  access_and_continuity: 'Access and continuity', co_creation_and_control: 'Creative participation',
  fantasy_depth: 'Story depth', novelty_and_collection: 'Variety and collecting',
  personal_continuity: 'Personal continuity', personal_recognition: 'Personal recognition',
  return_to_prior_choices: 'Returning to earlier choices', shared_creation: 'Shared creation',
  GENTLE_INITIATIVE: 'Gentle initiative', FAN_LED: 'Follow the fan’s lead',
  SPECIFIC_APPRECIATION: 'Acknowledge real contributions', CONSISTENT_RITUALS: 'Optional familiar routines',
  VARIETY_AND_SPONTANEITY: 'Variety and spontaneity', REFLECTIVE_CONVERSATION: 'Explore the current topic',
  DIRECT_AND_CONCISE: 'Direct and concise', LOW_PRESSURE_HIGH_AGENCY: 'Low pressure, more choice',
  CONSISTENT_WARMTH: 'Consistent warmth', ALLOW_CAREGIVING: 'Acknowledge caring gestures',
  RECOGNITION_AND_SELF_DISCLOSURE: 'Recognition and reciprocal conversation',
  PLAYFUL_CHALLENGE: 'Light reciprocal banter', SHARED_CREATION: 'Create together', NONE: 'None selected',
  SUGGEST_ONE_OPTIONAL_ACTIVITY: 'Suggest one optional activity', FOLLOW_FAN_TOPIC: 'Follow the fan’s topic',
  ACKNOWLEDGE_REAL_CONTRIBUTION: 'Acknowledge something the fan actually did',
  CLARIFY_OPTIONAL_RITUAL: 'Clarify whether an optional routine is welcome',
  INVITE_ONE_CREATIVE_CHOICE: 'Invite one creative choice', ANSWER_CURRENT_REQUEST: 'Answer the current request',
  FOLLOW_EXISTING_GRAPH: 'Let the current conversation flow lead', MAINTAIN_RELATIONSHIP: 'Maintain the conversation',
  FULL_AGREEMENT: 'Core and LLM agree', STRATEGY_DIFFERENCE: 'Different approaches',
  OBJECTIVE_DIFFERENCE: 'Different objectives', MILESTONE_DIFFERENCE: 'Different next steps',
  NO_COMPARABLE_STRATEGY: 'No comparable recommendation',
}
const title = (value) => labels[value] || (value ? String(value).toLowerCase().replaceAll('_', ' ') : 'Not recorded')
const date = (value) => value && !Number.isNaN(Date.parse(value)) ? new Date(value).toLocaleString() : 'Not recorded'
const percent = (value, factor = 1) => typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value * factor)}%` : 'Unknown'
const tabs = [['signals', 'Preferences'], ['plan', 'Strategy'], ['compare', 'Core vs LLM']]

function Evidence({ items = [], support, against }) {
  return <details className="fan-pref-evidence">
    <summary>Evidence · {support ?? '—'} supporting / {against ?? '—'} contradicting</summary>
    {!items.length ? <p>No source references recorded.</p> : <>
      <p className="fan-pref-muted">Selected evidence, not the complete conversation.</p>
      {items.map((item, i) => <article key={`${item.evidence_id}:${i}`}>
        <strong>{item.direction === 'contradict' ? 'Contradicts' : 'Supports'}</strong>
        {item.available ? <blockquote>{item.text}{item.truncated ? '…' : ''}</blockquote> : <p>Source unavailable in this snapshot. It may be deleted, outside the evidence window, or a non-message event.</p>}
        <small>{item.message_id ? `Message #${item.message_id} · ` : ''}{date(item.observed_at)}</small>
        {item.prompted_by_bot && <small>Response to a prompt — discounted evidence</small>}
        {item.scope === 'CURRENT_TURN' && <small>Applies to that turn only</small>}
      </article>)}
    </>}
  </details>
}

function ReviewLabel({ data, signal }) {
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [verdict, setVerdict] = useState('NEEDS_CONTEXT')
  const [note, setNote] = useState('')
  const reviewable = ['model_led_preference', 'fan_led_preference', 'playful_challenge_preference', 'co_creation_preference', 'ritual_preference', 'spontaneity_preference', 'appreciation_preference', 'reflective_conversation_preference', 'concise_exchange_preference', 'autonomy_preference'].includes(signal)
  if (!data.can_review || !reviewable) return null
  const save = async () => {
    setBusy(true); setFeedback('')
    try {
      await apiJson(`/bot/models/${data.model_id}/fans/${data.fan_id}/preferences/reviews`, {
        method: 'POST', suppressErrorToast: true,
        body: JSON.stringify({ state_version: data.state_version, signal, verdict, note, request_id: crypto.randomUUID() }),
      })
      setFeedback('QA label saved. No live behavior changed.')
    } catch { setFeedback('Could not save. The snapshot may have changed; refresh before trying again.') }
    finally { setBusy(false) }
  }
  return <details className="fan-pref-evidence"><summary>Review this hypothesis</summary>
    <p className="fan-pref-muted">Human QA only · does not overwrite evidence or become a live instruction.</p>
    <label>Verdict<select value={verdict} onChange={(e) => setVerdict(e.target.value)} disabled={busy}>
      <option value="NEEDS_CONTEXT">Needs context</option><option value="CONFIRM">Supported</option><option value="REJECT">Incorrect interpretation</option>
    </select></label>
    <label>Optional review note<textarea value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} disabled={busy} rows={2} /></label>
    <button className="v2-btn v2-btn-ghost" type="button" disabled={busy || data.stale} onClick={save}>{busy ? 'Saving…' : `Save label for v${data.state_version}`}</button>
    {feedback && <p role="status">{feedback}</p>}
  </details>
}

function Signals({ data }) {
  if (!data.signals?.length) return <p className="fan-pref-empty">Not enough evidence yet. Missing information does not mean no interest.</p>
  return <>
    <p className="fan-pref-muted">These are revisable preferences, not diagnoses. Confidence is an internal estimate, not a statistical probability.</p>
    {data.signals.map((signal) => <article className="fan-pref-card" key={`${signal.group}:${signal.signal}`}>
      <div className="fan-pref-card-head"><h4>{title(signal.signal)}</h4><span className={`fan-pref-tag ${signal.stable ? 'is-stable' : ''}`}>{signal.stable ? 'Stable signal' : 'Tentative'}</span></div>
      <small>{signal.group === 'value_drivers' ? 'What the fan values' : 'Communication preference'}</small>
      <dl>
        <div><dt>Confidence</dt><dd>{percent(signal.confidence, 100)}</dd></div>
        <div><dt>Supporting balance</dt><dd>{percent(signal.score)}</dd></div>
        <div><dt>Evidence groups</dt><dd>{signal.independent_group_count ?? 'Unknown'}</dd></div>
      </dl>
      <Evidence items={signal.evidence} support={signal.supporting_evidence_count} against={signal.contradicting_evidence_count} />
      <ReviewLabel key={`${data.model_id}:${data.fan_id}:${data.state_version}:${signal.signal}`} data={data} signal={signal.signal} />
    </article>)}
    {data.evidence_scan_limited && <p className="fan-pref-muted">Evidence previews are limited to the latest 400 recorded events.</p>}
  </>
}

function Strategy({ data }) {
  const plan = data.plan
  if (!plan) return <p className="fan-pref-empty">No interaction plan recorded. The extension may be disabled or still collecting evidence.</p>
  return <article className="fan-pref-card">
    <div className="fan-pref-card-head"><h4>{title(plan.relationship_strategy)}</h4><span className="fan-pref-tag">{plan.status === 'BLOCKED' ? 'Blocked' : 'Proposal'}</span></div>
    {(plan.stale || data.stale) && <p className="fan-pref-warning">Historical or expired proposal — not a current instruction.</p>}
    <p>{title(plan.micro_goal)}</p>
    {!!plan.secondary_strategies?.length && <><h5>Complementary approaches</h5><ul>{plan.secondary_strategies.map((s) => <li key={s}>{title(s)}</li>)}</ul></>}
    {!!plan.clarification_signals?.length && <><h5>Needs clarification</h5><ul>{plan.clarification_signals.map((s) => <li key={s}>{title(s)}</li>)}</ul></>}
    {!!plan.reason_codes?.length && <><h5>Why this approach</h5><ul>{plan.reason_codes.map((s) => <li key={s}>{title(s)}</li>)}</ul></>}
    <details className="fan-pref-evidence"><summary>Boundaries and version</summary>
      <ul>{plan.stop_conditions?.map((s) => <li key={s}>{title(s)}</li>)}</ul>
      <small>State v{plan.state_version ?? '—'} · expires {date(plan.expires_at)}</small>
    </details>
    <p className="fan-pref-muted">Proposed direction only. No activity, message or question has been performed by this panel.</p>
  </article>
}

function Directive({ name, value, stale }) {
  return <article className="fan-pref-card"><h4>{name}</h4>
    {!value ? <p>No recommendation recorded.</p> : <>
      {(stale || value.stale) && <span className="fan-pref-tag">Historical snapshot</span>}
      <dl><div><dt>Objective</dt><dd>{title(value.primary_objective)}</dd></div>
        <div><dt>Approach</dt><dd>{title(value.relationship_strategy)}</dd></div>
        <div><dt>Next step</dt><dd>{title(value.current_milestone)}</dd></div>
        <div><dt>Commercial goal</dt><dd>{title(value.commercial_objective)}</dd></div>
        <div><dt>Horizon</dt><dd>{value.horizon_turns_min ?? '—'}–{value.horizon_turns_max ?? '—'} turns</dd></div></dl>
      {!!value.avoid_turn_actions?.length && <p className="fan-pref-muted">Avoid: {value.avoid_turn_actions.map(title).join(', ')}</p>}
    </>}
  </article>
}

function Comparison({ data }) {
  return <>
    {data.comparison ? <div className="fan-pref-comparison"><strong>{title(data.comparison.status)}</strong>
      {data.comparison.stale && <p className="fan-pref-warning">Historical comparison; one or both versions are outdated.</p>}
      {!!data.comparison.difference_codes?.length && <p>Differences: {data.comparison.difference_codes.map(title).join(', ')}</p>}
    </div> : <p className="fan-pref-muted">No comparison yet. The separate LLM advisor may be disabled or may not have run.</p>}
    <Directive name="Deterministic Core" value={data.core} stale={data.stale} />
    <Directive name="LLM advisor" value={data.llm} stale={data.stale} />
    <p className="fan-pref-muted">Agreement does not prove correctness; neither recommendation changes live replies.</p>
  </>
}

export default function FanPreferencesPanel({ modelId, fanId }) {
  const key = `${modelId}:${fanId}`
  const id = useId()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('signals')
  const [refresh, setRefresh] = useState(0)
  const [state, setState] = useState({ key: '', loading: false, data: null, error: '' })
  useEffect(() => {
    if (!open) return undefined
    const controller = new AbortController()
    let alive = true
    setState({ key, loading: true, data: null, error: '' })
    apiJson(`/bot/models/${modelId}/fans/${fanId}/preferences`, { signal: controller.signal, suppressErrorToast: true })
      .then((data) => {
        if (!alive) return
        if (Number(data.model_id) !== Number(modelId) || Number(data.fan_id) !== Number(fanId)) throw new Error('Snapshot scope mismatch')
        setState({ key, loading: false, data, error: '' })
      }).catch(() => { if (alive) setState({ key, loading: false, data: null, error: 'Preferences are unavailable. Check diagnostics access or try again.' }) })
    return () => { alive = false; controller.abort() }
  }, [open, key, modelId, fanId, refresh])
  const data = state.key === key ? state.data : null
  const loading = state.key !== key || state.loading
  const error = state.key === key ? state.error : ''
  return <details className="v2-inspector-section fan-preferences-panel" onToggle={(e) => setOpen(e.currentTarget.open)}>
    <summary><span>Preferences &amp; strategy</span><span className="fan-pref-tag">Shadow</span></summary>
    {open && <div className="fan-pref-body">
      <p className="fan-pref-notice">Shadow diagnostics · QA labels do not change replies, offers or prices</p>
      {loading ? <p role="status">Loading preferences…</p> : error ? <p role="alert">{error}</p> : data && <>
        <div className="fan-pref-meta"><span>V4 collection: {data.collection_enabled ? 'enabled' : 'disabled'}</span><span>Snapshot: {date(data.updated_at)}</span></div>
        {data.stale && <p className="fan-pref-warning">The source context is over a day old or undated. Treat this as historical evidence.</p>}
        {data.status === 'scope_unavailable' ? <p className="fan-pref-empty">Ownership could not be verified. No previous tenant’s data is shown.</p>
          : data.status === 'no_data' ? <p className="fan-pref-empty">No shadow profile recorded yet. Opening this panel does not start analysis or enable the module.</p>
            : <>
              <div className="fan-pref-tabs" role="tablist" aria-label="Preference diagnostics">
                {tabs.map(([value, label], index) => <button key={value} type="button" role="tab" id={`${id}-${value}`} aria-controls={`${id}-content`} aria-selected={tab === value} tabIndex={tab === value ? 0 : -1}
                  onClick={() => setTab(value)} onKeyDown={(e) => {
                    const next = e.key === 'ArrowRight' ? (index + 1) % tabs.length : e.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null
                    if (next !== null) { e.preventDefault(); setTab(tabs[next][0]); document.getElementById(`${id}-${tabs[next][0]}`)?.focus() }
                  }}>{label}</button>)}
              </div>
              <div id={`${id}-content`} role="tabpanel" aria-labelledby={`${id}-${tab}`} tabIndex={0}>
                {tab === 'signals' ? <Signals data={data} /> : tab === 'plan' ? <Strategy data={data} /> : <Comparison data={data} />}
              </div>
            </>}
      </>}
      <button className="v2-btn v2-btn-ghost fan-pref-refresh" type="button" disabled={loading} onClick={() => setRefresh((v) => v + 1)}>Refresh snapshot</button>
    </div>}
  </details>
}
